// Roadmap 2: turns Claude's reply into a proposed workflow.
import { CODE_STEP } from "../../../engine";
import { allIds, nextId } from "../builder/editorState";
import { fillHiddenRows, hiddenValue } from "./promptMask";

const kindOf = (field) => field?.kind || "text";

// A value the user already filled in for a field with this name and kind:
// from the same step first, then from any other step (Claude may move a
// value to another step when it revises the workflow).
const filledValue = (codeSteps, stepId, field) => {
  const ordered = [...codeSteps.filter((s) => s.id === stepId), ...codeSteps.filter((s) => s.id !== stepId)];
  for (const step of ordered) {
    const value = step.params?.values?.[field.name];
    const before = (step.params?.valueFields || []).find((f) => f.name === field.name);
    if (value !== undefined && before && kindOf(before) === kindOf(field)) return value;
  }
  return undefined;
};

// Default output name: "output file N" with the lowest unused N.
const defaultFileName = (taken) => {
  let n = 1;
  while (taken.has(`output file ${n}`)) n++;
  return `output file ${n}`;
};

// No output list in the reply: outputs that wrote the old last step (or a
// table that no longer exists) follow the new last step; one is added when
// there is none.
const followLastStep = (base, steps, lastId) => {
  const oldLastId = base.steps[base.steps.length - 1]?.id;
  const tableIds = new Set([...base.sources.map((s) => s.id), ...steps.map((s) => s.id)]);
  const outputs = base.outputs.map((output) => ({
    ...output,
    sheets: (output.sheets || []).map((sheet) => (lastId && (!tableIds.has(sheet.from) || sheet.from === oldLastId) ? { ...sheet, from: lastId } : sheet)),
  }));
  if (!outputs.length && lastId) {
    outputs.push({
      id: nextId("output", allIds({ ...base, steps })),
      fileName: "output file 1",
      format: "xlsx",
      sheets: [{ name: "Sheet1", from: lastId, writeHeader: true }],
    });
  }
  return outputs;
};

// The reply's complete list of output files. An existing output (same id)
// keeps its settings, and a sheet that still writes the same table keeps its
// column layout; new outputs get the next free id and a default name.
const listedOutputs = (base, steps, listed) => {
  const before = new Map(base.outputs.map((output) => [output.id, output]));
  const ids = allIds({ ...base, steps, outputs: [] });
  // Names already chosen anywhere in the list, so defaults never clash with them.
  const names = new Set(listed.map((o) => (o.fileName || (o.id && before.get(o.id)?.fileName) || "").toLowerCase()).filter(Boolean));
  return listed.map((proposed) => {
    const old = proposed.id ? before.get(proposed.id) : null;
    const id = old ? old.id : nextId("output", ids);
    ids.push(id);
    const fileName = proposed.fileName || old?.fileName || defaultFileName(names);
    names.add(fileName.toLowerCase());
    const sheets = proposed.sheets.map((sheet, index) => {
      const oldSheet = old?.sheets?.[index];
      const kept = oldSheet && oldSheet.from === sheet.from ? oldSheet : { writeHeader: true };
      return { ...kept, name: sheet.name || oldSheet?.name || `Sheet${index + 1}`, from: sheet.from };
    });
    return { ...(old || {}), id, fileName, format: proposed.format || old?.format || "xlsx", sheets };
  });
};

// The proposed workflow: the reply's code steps (keeping trail colours of
// steps that keep their id, and every value the user already filled in) and
// outputs pointed at the new last step.
export const buildDraft = (base, reply) => {
  const codeSteps = base.steps.filter((s) => s.type === CODE_STEP);
  const previous = new Map(codeSteps.map((s) => [s.id, s]));
  const steps = reply.steps.map((proposed) => {
    const before = previous.get(proposed.id);
    const values = {};
    proposed.values.forEach((field) => {
      const value = filledValue(codeSteps, proposed.id, field);
      if (value !== undefined) values[field.name] = value;
    });
    return {
      ...(before ? { color: before.color, linkColors: before.linkColors, note: before.note, disabled: before.disabled } : {}),
      id: proposed.id,
      type: CODE_STEP,
      label: proposed.label,
      inputs: proposed.inputs,
      params: { summary: proposed.summary, code: proposed.code, codeSig: proposed.codeSig, labelSig: proposed.labelSig, valueFields: proposed.values, checks: proposed.checks || [], values },
    };
  });

  const lastId = steps[steps.length - 1]?.id;
  const outputs = reply.outputs ? listedOutputs(base, steps, reply.outputs) : followLastStep(base, steps, lastId);
  // Claude's summary of the whole workflow becomes its description.
  return { ...base, steps, outputs, description: reply.workflowSummary || base.description };
};

// Step values the user hasn't filled in yet: those named after a placeholder
// get the value it hides, and tables get the rows the reply proposed, with
// their placeholders filled the same way.
export const fillHiddenValues = (workflow, vault, reply) => ({
  ...workflow,
  steps: workflow.steps.map((step) => {
    const prefill = reply?.steps?.find((s) => s.id === step.id)?.prefill || {};
    const fields = (step.params?.valueFields || []).filter(
      (f) => step.params.values?.[f.name] === undefined && (f.kind === "table" ? Boolean(prefill[f.name]) : vault.byToken.has(f.name)),
    );
    if (step.type !== CODE_STEP || !fields.length) return step;
    const values = { ...step.params.values };
    fields.forEach((field) => {
      if (field.kind === "table") {
        values[field.name] = fillHiddenRows(prefill[field.name], vault);
        return;
      }
      const entry = vault.byToken.get(field.name);
      values[field.name] = field.kind === "number" ? hiddenValue({ ...entry, kind: "number" }) : String(entry.value);
    });
    return { ...step, params: { ...step.params, values } };
  }),
});
